// ============================================================
// Email ingest — incidental-PII scan + redaction (browser-side, pre-upload)
// Posture (2026-05-31): PRESERVE party/attorney/date identities (they ARE the
// evidence). FLAG only INCIDENTAL sensitive PII (SSN, account/card, DOB,
// third-party patient identifiers). User toggles `redact` per flag at the
// draft->approve gate; applyRedactions() masks toggled spans in the committed body.
// Strict-safe: tsconfig strict + noUncheckedIndexedAccess.
// ============================================================

import { EmailMessage, PiiFlag, PiiKind } from "./types";

interface Pattern { kind: PiiKind; re: RegExp; luhn?: boolean; }

const PATTERNS: Pattern[] = [
  { kind: "ssn", re: /\b\d{3}-\d{2}-\d{4}\b/g },
  { kind: "account", re: /\b(?:\d[ -]?){13,16}\b/g, luhn: true },
  { kind: "account", re: /\b\d{8,17}\b/g },
  { kind: "dob", re: /\b(?:DOB|date of birth)\b[:\s]*\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{2,4}/gi },
];

const PHI_CONTEXT = /\b(patient|diagnosis|prescription|Rx|medical record|MRN|HIPAA|treatment)\b/gi;

function luhnValid(s: string): boolean {
  const digits = s.replace(/\D/g, "");
  if (digits.length < 13) return false;
  let sum = 0, alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (alt) { d *= 2; if (d > 9) d -= 9; }
    sum += d; alt = !alt;
  }
  return sum % 10 === 0;
}

function mask(s: string): string {
  const t = s.replace(/\s+/g, " ").trim();
  if (t.length <= 4) return "*".repeat(t.length);
  return "*".repeat(Math.max(0, t.length - 4)) + t.slice(-4);
}

export function scanBody(body: string, opts: { includePhone?: boolean } = {}): PiiFlag[] {
  const flags: PiiFlag[] = [];
  const claimed: Array<{ s: number; e: number }> = [];
  const overlaps = (s: number, e: number) => claimed.some((c) => s < c.e && e > c.s);

  const patterns = [...PATTERNS];
  if (opts.includePhone) patterns.push({ kind: "phone", re: /\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/g });

  for (const p of patterns) {
    p.re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = p.re.exec(body))) {
      const full = m[0];
      if (full === undefined) break;
      const s = m.index, e = m.index + full.length;
      if (overlaps(s, e)) continue;
      if (p.luhn && !luhnValid(full)) continue;
      claimed.push({ s, e });
      flags.push({ kind: p.kind, span: [s, e], preview: mask(full), resolved: false, redact: false });
    }
  }

  PHI_CONTEXT.lastIndex = 0;
  const phi = PHI_CONTEXT.exec(body);
  const term = phi ? phi[0] : undefined;
  if (phi && term) {
    flags.push({
      kind: "third_party_phi",
      span: [phi.index, phi.index + term.length],
      preview: `medical-context term: "${term}" - review for third-party PHI`,
      resolved: false, redact: false,
    });
  }

  return flags.sort((a, b) => a.span[0] - b.span[0]);
}

export function scanThread(
  messages: EmailMessage[],
  opts: { includePhone?: boolean } = {}
): { messages: EmailMessage[]; flagCount: number; flaggedMessageIndices: number[] } {
  let flagCount = 0;
  const flagged: number[] = [];
  const out = messages.map((m) => {
    const pii_flags = scanBody(m.body, opts);
    if (pii_flags.length) { flagCount += pii_flags.length; flagged.push(m.message_index); }
    return { ...m, pii_flags };
  });
  return { messages: out, flagCount, flaggedMessageIndices: flagged };
}

/** Apply user-toggled redactions: mask spans where flag.redact === true.
 *  Replaces each toggled span with [REDACTED:<kind>]. Spans applied
 *  right-to-left so earlier offsets stay valid. */
export function applyRedactions(body: string, flags: PiiFlag[]): string {
  const toRedact = flags.filter((f) => f.redact).slice().sort((a, b) => b.span[0] - a.span[0]);
  let out = body;
  for (const f of toRedact) {
    const [s, e] = f.span;
    if (s >= 0 && e <= out.length && s < e) out = out.slice(0, s) + `[REDACTED:${f.kind}]` + out.slice(e);
  }
  return out;
}

/** Count flags toggled for redaction across a thread. */
export function redactionCount(messages: EmailMessage[]): number {
  return messages.reduce((n, m) => n + m.pii_flags.filter((f) => f.redact).length, 0);
}
